/**
 * The DOM side of the CPU path.
 *
 * Everything here is glue: turn an image or a canvas into a `Raster`, call the
 * isomorphic core, turn the result back into something the page can show. No
 * mathematics lives in this file.
 *
 * Milestone 2 adds a WebGL2 path that must produce the same bytes. This module
 * stays the reference.
 *
 * @module web/js/cpu
 */

import type { Raster, Stats, Vec3 } from '../../core/types.ts';
import type { Lut3d } from '../../core/luts.ts';
import { imageStats, meanOnlyStats, sampleMean, sampleStats, strideFor } from '../../core/stats.ts';
import { collapse } from '../../core/compose.ts';
import { applyAffineInPlace } from '../../core/apply.ts';
import { LAB_PRESET, DEFAULT_LEVEL, presetById } from '../../core/presets.ts';

/**
 * Statistics for a preset that measures nothing.
 *
 * The mean is a constant because a `needs: 'none'` preset anchors its output
 * level to one and cannot follow the image. The covariance is the identity,
 * which a `none` preset's `build` ignores anyway.
 */
const NO_STATS: Stats = {
  mean: DEFAULT_LEVEL,
  covariance: Float64Array.of(1, 0, 0, 0, 1, 0, 0, 0, 1),
  count: 0,
};

/** Pixels sampled when only the mean is needed. */
const MEAN_MAX_PIXELS = 20000;

/** Copy an `ImageData` into a `Raster` without sharing the buffer. */
export function rasterFromImageData(img: ImageData): Raster {
  const data = new Uint8ClampedArray(img.data.length);
  data.set(img.data);
  return { data, width: img.width, height: img.height };
}

/** Wrap an `ImageData`'s buffer in a `Raster` without copying. */
export function rasterView(img: ImageData): Raster {
  return { data: img.data, width: img.width, height: img.height };
}

/** Copy a `Raster` back into a new `ImageData`. */
export function imageDataFromRaster(r: Raster): ImageData {
  const data = new Uint8ClampedArray(r.data.length);
  data.set(r.data as unknown as ArrayLike<number>);
  return new ImageData(data, r.width, r.height);
}

/** Deep copy. */
export function cloneRaster(r: Raster): Raster {
  const data = new Uint8ClampedArray(r.data.length);
  data.set(r.data as unknown as ArrayLike<number>);
  return { data, width: r.width, height: r.height };
}

/**
 * Read an `<img>`, a `<video>` frame, a canvas, or an `ImageBitmap` into a fresh
 * `Raster`.
 *
 * `width`/`height` are the raster's size, not the source's: the source is drawn
 * scaled into that box. Omitting them uses the source's own size.
 */
export function rasterFromElement(
  source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
  width?: number,
  height?: number,
): Raster {
  const w = width ?? (source instanceof HTMLVideoElement ? source.videoWidth : source.width);
  const h = height ?? (source instanceof HTMLVideoElement ? source.videoHeight : source.height);
  if (!w || !h) throw new Error('source has no dimensions yet');
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2d context unavailable');
  ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
  return rasterFromImageData(ctx.getImageData(0, 0, w, h));
}

/** Draw a raster into a canvas, resizing the canvas to match. */
export function drawRaster(canvas: HTMLCanvasElement, r: Raster): void {
  canvas.width = r.width;
  canvas.height = r.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  const img = new ImageData(
    r.data instanceof Uint8ClampedArray ? r.data : new Uint8ClampedArray(r.data),
    r.width,
    r.height,
  );
  ctx.putImageData(img, 0, 0);
}

/** Statistics for a raster, subsampled so a large frame stays cheap. */
export function rasterStats(r: Raster, maxPixels = 20000): Stats {
  return sampleStats(r, strideFor(r, maxPixels));
}

/** Statistics over every pixel. Exact, and slower. */
export function rasterStatsExact(r: Raster): Stats {
  return imageStats(r);
}

let labLut: Lut3d | null = null;

/** The Lab preset's 3D LUT, built once per page. */
export function labLutCached(): Lut3d {
  if (!labLut) labLut = LAB_PRESET.buildLut();
  return labLut;
}

/**
 * Measure what a preset needs, and nothing more.
 *
 * A full covariance pass is nine products per sampled pixel; a mean pass is
 * three adds. On live video at 30 fps that is the difference between a cheap
 * frame and an expensive one, so the preset declares its requirement rather
 * than every caller guessing.
 */
export function statsFor(raster: Raster, presetId: string, precomputed?: Stats): Stats {
  const needs = presetById(presetId).needs ?? 'covariance';
  if (needs === 'none') return NO_STATS;
  if (needs === 'mean') {
    if (precomputed) return meanOnlyStats(precomputed.mean);
    return meanOnlyStats(sampleMean(raster, strideFor(raster, MEAN_MAX_PIXELS)));
  }
  return precomputed ?? rasterStats(raster);
}

/** What a caller may supply to a render beyond the pixels and the preset. */
export interface RenderOptions {
  /** Pre-measured statistics. Reused only if the preset needs them. */
  stats?: Stats;
  /**
   * Band-gain multiplier. Multiplies the preset's own gain, so `1` is the
   * preset as written and `2` stretches twice as hard. Ignored by presets that
   * build no affine (LAB).
   */
  gain?: number;
}

/**
 * Apply one preset to a raster and return a new raster.
 *
 * Fixed presets (YDS, LDS, …) have an image-independent matrix, so they need
 * only the mean, and the mean pass is three adds per sampled pixel instead of
 * the covariance's nine products. That is what makes them usable on live video.
 *
 * The caller may pass pre-measured statistics, which matters on video where the
 * same frame is measured once and rendered many times, and may scale the gain,
 * which is what makes a saved shot re-editable without re-measuring it.
 */
export function renderPreset(raster: Raster, presetId: string, options: RenderOptions = {}): Raster {
  const out = cloneRaster(raster);

  if (presetId === LAB_PRESET.id) {
    applyLabInPlace(out);
    return out;
  }

  const preset = presetById(presetId);
  const params = preset.build(statsFor(raster, presetId, options.stats));
  const gain = options.gain ?? 1;
  if (gain !== 1) params.gain = (params.gain ?? 1) * gain;
  applyAffineInPlace(out, collapse(params));
  return out;
}

/** The Lab preset: nonlinear, so it goes through the 3D LUT pixel by pixel. */
export function applyLabInPlace(raster: Raster): Raster {
  const lut = labLutCached();
  const { data } = raster;
  for (let px = 0; px < data.length; px += 4) {
    const rgb: Vec3 = Float64Array.of(data[px] as number, data[px + 1] as number, data[px + 2] as number);
    const o = LAB_PRESET.apply(lut, rgb);
    data[px] = o[0]!;
    data[px + 1] = o[1]!;
    data[px + 2] = o[2]!;
  }
  return raster;
}

/** Millisecond timer, for the "CPU path took N ms" readout. */
export function timeIt<T>(fn: () => T): { value: T; ms: number } {
  const t0 = performance.now();
  const value = fn();
  return { value, ms: performance.now() - t0 };
}
